import AppLayout from '@/Layouts/AppLayout';
import { Button } from '@/Components/ui/button';
import { Card, CardContent } from '@/Components/ui/card';
import { Badge } from '@/Components/ui/badge';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { BrandShowcase } from '@/Components/BrandShowcase';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/config/site';
import {
    ArrowRight,
    ArrowUpRight,
    DollarSign,
    Shield,
    Zap,
    Scale,
    Settings,
    Users,
    Star,
    TrendingUp,
    Clock,
    CheckCircle2,
} from 'lucide-react';

const stats = [
    { label: 'Active Traders', value: '2,847+' },
    { label: 'Total Funded', value: '$47.2M' },
    { label: 'Avg Payout', value: '24hrs' },
];

const trustIndicators = [
    { value: '$50M+', label: 'Funds Under Management', icon: DollarSign },
    { value: '10,000+', label: 'Active Traders', icon: Users },
    { value: '95%', label: 'Payout Success Rate', icon: TrendingUp },
    { value: '24/7', label: 'Premium Support', icon: Clock },
];

const fundingPrograms = [
    {
        name: 'Fast FX Plan',
        badge: 'Fast Track',
        features: [
            '$100K Max',
            '15% Profit Target',
            'Single Phase',
            '1:20 Leverage',
            '80% Profit Split',
        ],
    },
    {
        name: 'Regular Plan',
        badge: 'Most Popular',
        features: [
            '$100K Max',
            '10%+8% Targets',
            'Two Phases',
            '1:50 Leverage',
            '80% Profit Split',
        ],
        highlighted: true,
    },
    {
        name: 'Swing Plan',
        badge: 'Flexible',
        features: [
            '$100K Max',
            '10%+8% Targets',
            'Weekend Trading',
            'News Trading',
            '80% Profit Split',
        ],
    },
];

const features = [
    {
        icon: DollarSign,
        title: 'Instant Payouts',
        description: 'Receive your profits within 24 hours. No delays, no excuses — your money, your way.',
    },
    {
        icon: Shield,
        title: 'Risk Protection',
        description: 'Trade with confidence knowing our risk management framework protects your funded account.',
    },
    {
        icon: Zap,
        title: 'Lightning Fast',
        description: 'Ultra-low latency execution on all major pairs. Your edge starts with speed.',
    },
    {
        icon: Scale,
        title: 'Fair & Transparent',
        description: 'Clear rules, no hidden conditions. What you see is what you get — always.',
    },
    {
        icon: Settings,
        title: 'Flexible Trading',
        description: 'Trade your style. Scalping, swing, or day trading — we support how you trade best.',
    },
    {
        icon: Users,
        title: 'Expert Support',
        description: 'Dedicated account managers and 24/7 support from traders who understand the markets.',
    },
];

const testimonials = [
    {
        name: 'Michael Chen',
        role: 'Funded Trader',
        quote: `${siteConfig.brand.name} changed my trading career. The evaluation process was straightforward, and I received my first payout within 24 hours of requesting it. The platform is genuinely built for serious traders.`,
    },
    {
        name: 'Sarah Williams',
        role: 'Funded Trader',
        quote: `The transparency at ${siteConfig.brand.name} is unmatched. No hidden rules, no surprises. I've been funded for 6 months now and every payout has been processed on time. This is the real deal.`,
    },
    {
        name: 'David Martinez',
        role: 'Funded Trader',
        quote: `After trying three other prop firms, ${siteConfig.brand.name} is the one that stuck. The support team actually understands trading, and the profit split is among the best I've seen in the industry.`,
    },
];

export default function Home() {
    return (
        <AppLayout title={`${siteConfig.brand.name} - Premium Prop Trading Firm`}>
            {/* Hero */}
            <section className="relative min-h-screen flex items-center overflow-hidden">
                {/* Background effects */}
                <div className="absolute inset-0 bg-grid" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(79,143,139,0.12),transparent)]" />

                <div className="relative mx-auto max-w-7xl px-4 py-32 sm:px-6 lg:px-8 w-full">
                    <div className="mx-auto max-w-4xl text-center">
                        <FadeIn>
                            <Badge variant="outline" className="mb-8 border-primary/20 text-primary/90">
                                Premium Prop Trading
                            </Badge>
                        </FadeIn>

                        <FadeIn delay={0.1}>
                            <h1 className="text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl">
                                <span className="text-foreground">Trade with </span>
                                <span className="text-gradient">Confidence</span>
                            </h1>
                        </FadeIn>

                        <FadeIn delay={0.2}>
                            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground sm:text-xl leading-relaxed">
                                Access up to $100K in trading capital. Prove your skills, get funded,
                                and keep up to 80% of the profits.
                            </p>
                        </FadeIn>

                        <FadeIn delay={0.3}>
                            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                                <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                    <Button size="lg" className="px-8">
                                        Get Funded Today
                                        <ArrowUpRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </a>
                                <Link to="/funding/overview">
                                    <Button size="lg" variant="outline" className="px-8">
                                        View Programs
                                    </Button>
                                </Link>
                            </div>
                        </FadeIn>
                    </div>

                    <FadeIn delay={0.5} y={32}>
                        <div className="mx-auto mt-24 max-w-3xl">
                            <div className="grid grid-cols-3 divide-x divide-border rounded-2xl border border-border bg-card/50 backdrop-blur-sm">
                                {stats.map((stat) => (
                                    <div key={stat.label} className="px-6 py-6 text-center">
                                        <p className="text-2xl font-bold text-foreground sm:text-3xl">{stat.value}</p>
                                        <p className="mt-1 text-sm text-muted-foreground">{stat.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* Trust Indicators */}
            <Section className="border-t border-border bg-muted/50">
                <StaggerChildren className="grid grid-cols-2 gap-8 lg:grid-cols-4">
                    {trustIndicators.map((item) => (
                        <motion.div key={item.label} variants={staggerItem} className="text-center">
                            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                <item.icon className="h-5 w-5 text-primary" />
                            </div>
                            <p className="text-2xl font-bold text-foreground lg:text-3xl">{item.value}</p>
                            <p className="mt-1 text-sm text-muted-foreground">{item.label}</p>
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* Brand Showcase — 3D TEF mark */}
            <BrandShowcase />

            {/* Funding Programs */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Choose Your"
                        highlight="Funding Program"
                        description="Select the evaluation that matches your trading style. All programs include up to $100K in funded capital."
                    />
                </FadeIn>

                <StaggerChildren className="grid gap-6 md:grid-cols-3 lg:gap-8">
                    {fundingPrograms.map((program) => (
                        <motion.div key={program.name} variants={staggerItem}>
                            <Card className={`relative overflow-hidden h-full ${
                                program.highlighted
                                    ? 'border-primary/40 bg-primary/[0.03]'
                                    : ''
                            }`}>
                                {program.highlighted && (
                                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />
                                )}
                                <CardContent className="p-6 lg:p-8">
                                    <div className="mb-5 flex items-center justify-between">
                                        <h3 className="text-lg font-semibold text-foreground">{program.name}</h3>
                                        <Badge variant={program.highlighted ? 'default' : 'secondary'}>
                                            {program.badge}
                                        </Badge>
                                    </div>

                                    <ul className="mb-8 space-y-3">
                                        {program.features.map((feature) => (
                                            <li key={feature} className="flex items-center text-sm text-muted-foreground">
                                                <CheckCircle2 className="mr-3 h-4 w-4 text-primary/60 shrink-0" />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>

                                    <Link to="/funding/overview" className="block">
                                        <Button
                                            className="w-full"
                                            variant={program.highlighted ? 'default' : 'outline'}
                                        >
                                            Learn More
                                            <ArrowRight className="ml-2 h-4 w-4" />
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* Why Choose Us */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Why Choose"
                        highlight={`${siteConfig.brand.name}`}
                        description="We built a prop firm that puts traders first."
                    />
                </FadeIn>

                <StaggerChildren className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {features.map((feature) => (
                        <motion.div key={feature.title} variants={staggerItem}>
                            <Card className="h-full border-border hover:border-primary/20 transition-all duration-300">
                                <CardContent className="p-6 lg:p-8">
                                    <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                        <feature.icon className="h-5 w-5 text-primary" />
                                    </div>
                                    <h3 className="mb-2 text-base font-semibold text-foreground">{feature.title}</h3>
                                    <p className="text-sm leading-relaxed text-muted-foreground">{feature.description}</p>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* Testimonials */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Trusted by"
                        highlight="Traders Worldwide"
                        description={`Hear from funded traders who have experienced the ${siteConfig.brand.name} difference.`}
                    />
                </FadeIn>

                <StaggerChildren className="grid gap-6 md:grid-cols-3 lg:gap-8">
                    {testimonials.map((testimonial) => (
                        <motion.div key={testimonial.name} variants={staggerItem}>
                            <Card className="h-full border-border">
                                <CardContent className="p-6 lg:p-8">
                                    <div className="mb-4 flex gap-1">
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <Star key={i} className="h-4 w-4 fill-primary/80 text-primary/80" />
                                        ))}
                                    </div>
                                    <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
                                        "{testimonial.quote}"
                                    </p>
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                                            {testimonial.name.split(' ').map(n => n[0]).join('')}
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-foreground">{testimonial.name}</p>
                                            <p className="text-xs text-muted-foreground">{testimonial.role}</p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="bg-muted/30 border-t border-border">
                <FadeIn>
                    <div className="mx-auto max-w-3xl text-center">
                        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
                            Ready to Elevate{' '}
                            <span className="text-gradient">Your Trading?</span>
                        </h2>
                        <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
                            Join thousands of funded traders and start your journey to consistent profits today.
                        </p>
                        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg" className="px-8">
                                    Get Funded Today
                                    <ArrowUpRight className="ml-2 h-4 w-4" />
                                </Button>
                            </a>
                            <Link to="/funding/overview">
                                <Button size="lg" variant="outline" className="px-8">
                                    View Programs
                                </Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
